import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, TrendingUp, Star, Copy, UserPlus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { API } from '../config';
const o = { withCredentials: true };

const CopyTrading = () => {
  const { user } = useAuth();
  const [traders, setTraders] = useState([]);
  const [myFollows, setMyFollows] = useState([]);
  const [myCopiers, setMyCopiers] = useState(null);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFollow, setShowFollow] = useState(null);
  const [followForm, setFollowForm] = useState({ exchange_id: '', amount: '50', tx_hash: '' });
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [step, setStep] = useState(1);

  const load = async () => {
    try {
      const [t, f, c, e] = await Promise.all([
        axios.get(`${API}/copy-trading/traders`, o).catch(() => ({ data: { traders: [] } })),
        axios.get(`${API}/copy-trading/my-follows`, o).catch(() => ({ data: { follows: [] } })),
        axios.get(`${API}/copy-trading/my-copiers`, o).catch(() => ({ data: null })),
        axios.get(`${API}/exchanges/keys`, o).catch(() => ({ data: { keys: [] } })),
      ]);
      setTraders(t.data.traders || []);
      setMyFollows(f.data.follows || []);
      setMyCopiers(c.data);
      setExchanges(e.data.keys || []);
      const pi = await axios.get(`${API}/payment/info`, o).catch(() => ({ data: null }));
      setPaymentInfo(pi.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const requestTrader = async () => {
    try {
      const r = await axios.post(`${API}/copy-trading/request`, {}, o);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const submitCopyPayment = async () => {
    if (!followForm.tx_hash.trim()) { toast.error('Pon el hash de la transacción'); return; }
    try {
      await axios.post(`${API}/copy-trading/request-follow`, null, {
        params: { trader_id: showFollow.user_id, exchange_id: followForm.exchange_id,
                  amount: followForm.amount, tx_hash: followForm.tx_hash },
        ...o });
      toast.success('Solicitud enviada. El admin verificará el pago.');
      setShowFollow(null);
      setStep(1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const unfollow = async (traderId) => {
    try {
      await axios.post(`${API}/copy-trading/unfollow?trader_id=${traderId}`, {}, o);
      toast.success('Dejaste de copiar');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-full"><div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div></div></Layout>;

  const isTrader = traders.some(t => t.user_id === user?.user_id);
  const followedIds = new Set(myFollows.map(f => f.trader_id));

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight">Copy Trading</h1>
            <p className="text-[#A1A1AA] mt-1">Copia las operaciones de traders verificados automáticamente</p>
          </div>
          {!isTrader && (
            <button onClick={requestTrader}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#FFD600] to-[#FFB800] text-black text-sm font-bold">
              <Star size={16} /> Solicitar ser Copy Trader
            </button>
          )}
        </div>

        {/* Mis copias activas */}
        {myFollows.length > 0 && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Copy size={18} /> Mis Copias Activas</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {myFollows.map(f => (
                  <div key={f.follow_id || f.trader_id} className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-[#27272A]">
                    <div>
                      <p className="text-white font-medium">{f.trader_name}</p>
                      <p className="text-xs text-[#A1A1AA]">{f.exchange_id} · ${f.amount}/operación · {f.trades_copied || 0} trades copiados</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm mono font-bold ${(f.total_pnl || 0) >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                        ${(f.total_pnl || 0).toFixed(2)}
                      </span>
                      <button onClick={() => unfollow(f.trader_id)}
                        className="px-2 py-1 rounded text-xs text-red-400 border border-red-500/20 hover:bg-red-500/10">
                        <XCircle size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mis seguidores (si soy trader) */}
        {myCopiers && myCopiers.copiers && myCopiers.copiers.length > 0 && (
          <Card className="bg-[#121212] border-[#00D4FF]/20">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Users size={18} className="text-[#FFD600]" /> Tus Seguidores (eres Copy Trader)</CardTitle></CardHeader>
            <CardContent>
              <p className="text-[#FFD600] font-bold text-lg mb-2">Ganancia por copias: ${myCopiers.total_earnings?.toFixed(2)}</p>
              <div className="space-y-1">
                {myCopiers.copiers.map((c, i) => (
                  <div key={i} className="flex justify-between text-sm p-2 bg-[#1E1E1E] rounded">
                    <span className="text-white">{c.follower_name}</span>
                    <span className="text-[#A1A1AA]">${c.amount}/op · {c.trades_copied || 0} copiados</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de traders */}
        <div>
          <h2 className="text-xl font-bold text-white mb-3">Traders Verificados</h2>
          {traders.length === 0 ? (
            <Card className="bg-[#121212] border-[#27272A]">
              <CardContent className="py-8 text-center">
                <Users className="mx-auto text-[#A1A1AA] mb-3" size={40} />
                <p className="text-[#A1A1AA]">Aún no hay traders aprobados. Solicita ser el primero.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {traders.map(t => (
                <Card key={t.user_id} className={`bg-[#121212] border transition-all hover:shadow-lg ${followedIds.has(t.user_id) ? 'border-[#00D4FF]/40' : 'border-[#27272A] hover:border-[#3F3F46]'}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00D4FF] to-[#00FF88] flex items-center justify-center text-black font-bold text-lg">
                        {t.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-bold">{t.name}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00FF88]/15 text-[#00FF88] font-bold">VERIFICADO</span>
                          <span className="text-xs text-[#A1A1AA]">{t.followers || 0} seguidores</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-[#1E1E1E] rounded p-2 text-center">
                        <p className="text-[10px] text-[#A1A1AA]">Win Rate</p>
                        <p className={`text-sm font-bold mono ${(t.recent_win_rate || t.win_rate || 0) >= 60 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                          {(t.recent_win_rate || t.win_rate || 0).toFixed(0)}%
                        </p>
                      </div>
                      <div className="bg-[#1E1E1E] rounded p-2 text-center">
                        <p className="text-[10px] text-[#A1A1AA]">P&L</p>
                        <p className={`text-sm font-bold mono ${(t.recent_pnl || t.total_pnl || 0) >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                          ${(t.recent_pnl || t.total_pnl || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-[#1E1E1E] rounded p-2 text-center">
                        <p className="text-[10px] text-[#A1A1AA]">Trades</p>
                        <p className="text-sm font-bold text-white mono">{t.recent_trades || t.total_trades || 0}</p>
                      </div>
                    </div>

                    {t.user_id === user?.user_id ? (
                      <p className="text-center text-xs text-[#A1A1AA]">Eres tú</p>
                    ) : followedIds.has(t.user_id) ? (
                      <button onClick={() => unfollow(t.user_id)}
                        className="w-full py-2 rounded-lg text-sm font-bold border border-red-500/30 text-red-400 hover:bg-red-500/10">
                        Dejar de copiar
                      </button>
                    ) : (
                      <button onClick={() => { setShowFollow(t); setFollowForm({ exchange_id: exchanges[0]?.exchange_id || '', amount: '50', tx_hash: '' }); setStep(1); }}
                        className="w-full py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-[#00CC77] to-[#00FF88] text-black">
                        <Copy size={14} className="inline mr-1" /> Copiar Trader
                      </button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <Card className="bg-[#1E1E1E] border-[#27272A]">
          <CardContent className="py-4 text-sm text-[#A1A1AA] space-y-1">
            <p><b className="text-white">¿Cómo funciona?</b> Cuando un trader verificado abre o cierra una posición, la misma operación se ejecuta automáticamente en tu exchange con el monto que elegiste.</p>
            <p><b className="text-white">Costo:</b> $5 por operación copiada (se le paga al trader). Las ganancias generadas aplican el 20% de la plataforma como cualquier trade.</p>
            <p><b className="text-white">Riesgo:</b> El copy trading implica riesgo real. Los resultados pasados no garantizan ganancias futuras.</p>
          </CardContent>
        </Card>
      </div>

      {/* Dialog seguir trader */}
      <Dialog open={!!showFollow} onOpenChange={() => { setShowFollow(null); setStep(1); }}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader><DialogTitle>Copiar a {showFollow?.name}</DialogTitle></DialogHeader>
          {showFollow && (
            <div className="space-y-4">
              <div className="bg-[#1E1E1E] p-3 rounded border border-[#27272A] text-sm">
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Win Rate:</span><span className="text-[#00FF88] font-bold">{(showFollow.recent_win_rate || showFollow.win_rate || 0).toFixed(0)}%</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">P&L reciente:</span><span className={`font-bold ${(showFollow.recent_pnl || 0) >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>${(showFollow.recent_pnl || showFollow.total_pnl || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Costo por copia:</span><span className="text-[#FFD600] font-bold">$5.00</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Trader recibe:</span><span className="text-white">$4.00 (80%)</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Plataforma:</span><span className="text-white">$1.00 (20%)</span></div>
              </div>

              {step === 1 && (<>
                <div>
                  <Label>Exchange para operar</Label>
                  <Select value={followForm.exchange_id} onValueChange={v => setFollowForm({...followForm, exchange_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Selecciona exchange" /></SelectTrigger>
                    <SelectContent>
                      {exchanges.map(ex => <SelectItem key={ex.key_id} value={ex.exchange_id}>{ex.exchange_id.toUpperCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Monto por operación (USDT)</Label>
                  <Input type="number" min="10" value={followForm.amount} onChange={e => setFollowForm({...followForm, amount: e.target.value})} placeholder="50" />
                </div>
                <button onClick={() => setStep(2)} disabled={!followForm.exchange_id}
                  className="w-full py-2.5 rounded-lg text-sm font-bold bg-[#00D4FF] text-black disabled:opacity-50">
                  Siguiente: Realizar pago
                </button>
              </>)}

              {step === 2 && (<>
                <div className="bg-[#FFD600]/10 border border-[#FFD600]/30 rounded p-3 space-y-2">
                  <p className="text-sm font-bold text-[#FFD600]">Pago de $5.00 USDT (BEP-20)</p>
                  <p className="text-xs text-[#A1A1AA]">Envía $5 USDT por BNB Smart Chain a esta wallet:</p>
                  <p className="text-sm mono text-white break-all bg-[#111] p-2 rounded">
                    {paymentInfo?.wallets_de_pago ? Object.values(paymentInfo.wallets_de_pago)[0] || 'Wallet no configurada' : 'Wallet no configurada'}
                  </p>
                  <p className="text-[10px] text-[#52525B]">Red: BNB Smart Chain (BEP-20)</p>
                </div>
                <div>
                  <Label>Hash de la transacción</Label>
                  <Input value={followForm.tx_hash} onChange={e => setFollowForm({...followForm, tx_hash: e.target.value})}
                    placeholder="0x..." />
                  <p className="text-[10px] text-[#52525B] mt-1">Copia el hash desde tu wallet o desde bscscan.com</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setStep(1)} className="flex-1 py-2 rounded-lg text-sm border border-[#27272A] text-[#A1A1AA]">Atrás</button>
                  <button onClick={submitCopyPayment} disabled={!followForm.tx_hash.trim()}
                    className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-gradient-to-r from-[#00CC77] to-[#00FF88] text-black disabled:opacity-50">
                    Enviar pago
                  </button>
                </div>
              </>)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CopyTrading;
